/** @format */
import { Subject } from '@toucan/components/organisms/header/helpers/subject';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface HeaderObserverState {
    /**
     * If the header should be visible
     */
    isVisible: boolean;

    /**
     * If the smaller header should be shown
     */
    isSmallHeader: boolean;
}

export type HeaderLockObserver = (args: HeaderObserverState) => void;

export enum HeaderLock {
    // Lock for the space where the large desktop header will always show
    TOP = 'top',

    // Lock for the space where the header should still show after scrolling down
    INITIAL = 'initial',

    // When the user is scrolling up
    SCROLL_UP = 'scroll-up',

    // When a basket item has been added
    BASKET_ITEM_ADDED = 'basket-item-added',
}

class HeaderLockSubject extends Subject<HeaderLockObserver> {
    private headerLocks = new Map<HeaderLock, Set<number>>();

    /**
     * Gets the current state of the header based on the locks present
     */
    public getHeaderState() {
        const isVisible = this.headerLocks.size !== 0;
        const isSmallHeader = !this.headerLocks.has(HeaderLock.TOP);

        return {
            isVisible,
            isSmallHeader,
        };
    }

    /**
     * Adds a lock, which will keep the header open until removed
     *
     * @param lock The lock that will keep the header open
     * @param id An id to associate with the lock. Useful
     * for when a lock can have multiple items related to it. (e.g BASKET_ITEM_ADDED)
     */
    public addLock(lock: HeaderLock, id: number = 0) {
        const existingLock = this.headerLocks.get(lock);

        if (existingLock && !existingLock.has(id)) {
            existingLock.add(id);
        } else if (!existingLock) {
            this.headerLocks.set(lock, new Set([id]));
        }

        this.notify(this.getHeaderState());
    }

    /**
     * Removes a lock, which may effect the header state
     *
     * @param lock The lock to remove
     * @param id An id to associate with the lock. Useful
     * for when a lock can have multiple items related to it. (e.g BASKET_ITEM_ADDED)
     */
    public removeLock(lock: HeaderLock, id: number = 0) {
        const lockSet = this.headerLocks.get(lock);
        if (lockSet) {
            lockSet.delete(id);
            if (lockSet.size === 0) {
                this.headerLocks.delete(lock);
            }
        }

        this.notify(this.getHeaderState());
    }
}

/**
 * A subject used to add & remove open locks on the Header
 */
const headerLockSubject = new HeaderLockSubject();

headerLockSubject.addLock(HeaderLock.TOP);
headerLockSubject.addLock(HeaderLock.INITIAL);

export default headerLockSubject;
